import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShoppingBag } from "lucide-react";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookDetail() {
  const { slug } = useParams();
  const [book, setBook] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    axios.get(`${API}/books/${slug}`)
      .then((response) => { setBook(response.data); setStatus("ready"); })
      .catch(() => { setBook(null); setStatus("error"); });
  }, [slug]);

  if (status === "loading") {
    return <div className="px-5 pb-28 pt-40 md:px-8" data-testid="book-detail-loading"><p className="mx-auto max-w-7xl rounded-[2rem] bg-petal/50 p-10">Opening the book...</p></div>;
  }

  if (status === "error" || !book) {
    return (
      <div className="px-5 pb-28 pt-40 md:px-8" data-testid="book-detail-error">
        <div className="mx-auto max-w-7xl rounded-[2rem] bg-petal p-10">
          <p className="font-display text-4xl italic">This page is not on the shelf.</p>
          <Link to="/books" data-testid="book-detail-error-back-link" className="mt-6 inline-flex rounded-full bg-cocoa px-6 py-3 text-sm text-white">Back to books</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 pb-28 pt-32 md:px-8 md:pt-40" data-testid="book-detail-page">
      <section className="mx-auto max-w-7xl">
        <Reveal>
          <Link to="/books" data-testid="book-detail-back-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-cocoa/55 transition-colors duration-300 hover:text-cocoa">
            <ArrowLeft size={15} /> Back to the shelf
          </Link>
        </Reveal>
        <div className="mt-12 grid items-center gap-14 lg:grid-cols-[0.85fr_1.15fr]">
          <Reveal className="relative">
            <div className="spotlight rounded-[3rem] p-8 md:p-12" style={{ "--spot": book.accent }}>
              <div className="arch-frame mx-auto aspect-[3/4] max-w-md overflow-hidden bg-white" data-testid="book-detail-image-frame">
                <img src={book.image} alt={`${book.title} cover`} data-testid="book-detail-image" className="h-full w-full" style={{ objectFit: book.image_fit === "contain" ? "contain" : "cover" }} />
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal delay={0.1}>
              <p className="text-xs uppercase tracking-[0.35em] text-cocoa/50" data-testid="book-detail-mood">{book.mood}</p>
              <KineticHeading testId="book-detail-title" lines={[book.title]} className="mt-6 font-display text-6xl font-light leading-[0.9] tracking-[-0.04em] md:text-8xl" />
              <p className="mt-8 max-w-2xl text-base leading-8 text-cocoa/68 md:text-lg" data-testid="book-detail-description">{book.description}</p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-10 rounded-[2rem] border border-cocoa/10 bg-white/70 p-7" data-testid="book-detail-excerpt-panel">
                <p className="text-xs uppercase tracking-[0.25em] text-cocoa/45">Excerpt</p>
                <p className="mt-4 font-display text-2xl italic leading-9 text-cocoa/80" data-testid="book-detail-excerpt">“{book.excerpt}”</p>
              </div>
            </Reveal>
            <Reveal delay={0.28}>
              <div className="mt-8" data-testid="book-detail-tropes">
                <p className="text-xs uppercase tracking-[0.25em] text-cocoa/45">Tropes</p>
                <div className="mt-4 flex flex-wrap gap-3">
                  {book.tropes.map((trope) => <span key={trope} data-testid={`book-trope-${trope.toLowerCase().replaceAll(" ", "-").replaceAll("'", "")}`} className="rounded-full border border-cocoa/10 bg-petal/45 px-4 py-2 text-sm text-cocoa/70">{trope}</span>)}
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.35}>
              <div className="mt-10 flex flex-wrap gap-4" data-testid="book-detail-actions">
                {book.buy_url ? (
                  <a href={book.buy_url} target="_blank" rel="noreferrer" data-testid="book-detail-buy-link" className="inline-flex items-center gap-3 rounded-full bg-cocoa px-7 py-4 text-sm text-white transition-transform duration-300 hover:scale-105">
                    Buy on Amazon <ShoppingBag size={16} />
                  </a>
                ) : (
                  <button type="button" disabled data-testid="book-detail-buy-disabled" className="cursor-not-allowed rounded-full border border-cocoa/10 bg-cocoa/5 px-7 py-4 text-sm text-cocoa/40">
                    Purchase link coming soon
                  </button>
                )}
                <Link to="/contact" data-testid="book-detail-contact-link" className="rounded-full border border-cocoa/15 px-7 py-4 text-sm transition-colors duration-300 hover:border-cocoa hover:bg-petal/50">
                  Ask Morgan
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
